import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ButtonHTMLAttributes, ReactElement } from 'react';
import { cn } from '../felles/cn.js';

/**
 * Button, in the shadcn/ui style: the source lives in this repository rather
 * than inside a dependency, so its markup and its accessibility behaviour are
 * ours to audit and to fix (§1). Radix's Slot gives `asChild`, which lets a link
 * render with button styling WITHOUT becoming a `<div onClick>` — the element
 * stays semantically correct for assistive technology.
 */
const knappVarianter = cva(
  // Min height 44px: WCAG 2.5.5 target size, and the difference between usable
  // and not on a touch device.
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-60',
  {
    variants: {
      variant: {
        primar: 'bg-merke text-merke-tekst hover:bg-merke-sterk',
        sekundar: 'border border-kant bg-flate text-tekst hover:bg-flate-dempet',
        stille: 'text-merke hover:bg-merke-svak',
      },
      storrelse: {
        normal: 'text-sm',
        stor: 'min-h-12 px-6 text-base',
      },
    },
    defaultVariants: { variant: 'primar', storrelse: 'normal' },
  },
);

export interface KnappProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof knappVarianter> {
  asChild?: boolean;
}

export function Knapp({
  className,
  variant,
  storrelse,
  asChild = false,
  type = 'button',
  ...props
}: KnappProps): ReactElement {
  const Komponent = asChild ? Slot : 'button';
  return (
    <Komponent
      // Explicitly defaulted: an unset `type` inside a form is `submit`, which
      // makes an ordinary button silently submit the form.
      {...(asChild ? {} : { type })}
      className={cn(knappVarianter({ variant, storrelse }), className)}
      {...props}
    />
  );
}
